import * as React from 'react'

import {
  BorderStyle,
  Box,
  Button,
  type MediumIcon,
  Tag,
  Text,
} from '@factorialco/deprecated-design-system'
import {
  TimesheetEstimatedTimeFragment,
  TimesheetReviewsStatsFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { EventTypes, dispatch } from 'modules/core/lib/pubSub'
import app from 'modules/core/store/models/app'

import type Store from 'modules/attendance/components/ClockIn/Store'
import { useAutofillAttendanceShifts } from 'modules/attendance/hooks/shifts/useAutofillAttendanceShifts'
import { usePeriodApproval } from 'modules/attendance/hooks/usePeriodApproval'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useAttendancePolicies } from 'modules/attendance/lib/attendancePolicies'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import { PeriodState } from 'modules/attendance/lib/types'
import { useTimeTrackingFeatureAvailability } from 'modules/attendance/lib/useTimeTrackingFeatureAvailability'
import EmployeesSettingsUtils from 'modules/attendance/lib/utils/EmployeesSettingsUtils'

interface Props {
  reviewsStats: TimesheetReviewsStatsFragment | null
  store: Store
  toggleExpandAll: (value: boolean) => () => void
  expandAll: boolean
}

const PeriodStatus = ({ store, reviewsStats, toggleExpandAll, expandAll }: Props) => {
  const { stringDateFrom: startOn, stringDateTo: endOn } = useAttendanceDateNavigator()
  const state = reviewsStats?.state
  const { data } = useAttendancePolicies()
  const isClockInSection = window.location.pathname.includes('attendance/clock-in')
  const canApprovePeriod = !!data?._policies?.companyApproveAttendance?.can
  const { employeesSettings, estimatedTimes, attendanceEmployeesPolicyId } = useTimesheetData()

  const showApproveButton =
    !isClockInSection && canApprovePeriod && state !== PeriodState.in_progress
  const hasAutofillTimesheet = useTimeTrackingFeatureAvailability(
    'time_tracking_autofill_timesheet'
  )

  const hasAutofillEnabled = EmployeesSettingsUtils.hasAutofillTimesheet(
    employeesSettings,
    attendanceEmployeesPolicyId
  )

  const isEstimationByShifts = estimatedTimes.some(
    (estimatedTime: TimesheetEstimatedTimeFragment) => !!estimatedTime.shifts.length
  )

  const canAutofill =
    !!data?._policies?.shiftAutofill?.can && !!data?.employee?._policies?.editAttendance?.can

  const showAutofillButton =
    hasAutofillTimesheet &&
    hasAutofillEnabled &&
    canAutofill &&
    isEstimationByShifts &&
    state != PeriodState.approved

  const [showAutofillModal, setShowAutofillModal] = React.useState(false)
  const { autofillTimesheet, loading } = useAutofillAttendanceShifts()

  const handleAutofill = async () => {
    const payload = {
      employeeIds: [store.employeeId],
      startOn,
      endOn,
    }

    const result = await autofillTimesheet(payload)
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      dispatch({
        type: EventTypes.ATTENDANCE_SHIFT_SAVED,
      })
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      setShowAutofillModal(false)
    }
    return handled
  }

  const stateInfo = () => {
    switch (state) {
      case PeriodState.approved:
        return {
          title: i18n.t('clock_in.header.states.approved_title'),
          description: i18n.t('clock_in.header.states.approved_description'),
          type: Tag.Types.lime,
        }

      case PeriodState.pending:
        return {
          title: i18n.t('clock_in.header.states.pending_title'),
          description: i18n.t('clock_in.header.states.pending_description'),
          type: Tag.Types.champagne,
        }

      case PeriodState.in_progress:
        return {
          title: i18n.t('clock_in.header.states.in_progress_title'),
          description: i18n.t('clock_in.header.states.in_progress_description'),
          type: Tag.Types.grey,
        }

      default:
        return {
          title: '',
          description: '',
          type: Tag.Types.grey,
        }
    }
  }

  const { title, description, type } = stateInfo()

  const { approvePeriod, disapprovePeriod } = usePeriodApproval({ employeeId: store.employeeId })

  const handleConfirmPeriod = async () => {
    state === 'approved' ? await disapprovePeriod() : await approvePeriod()
  }

  const label = () => {
    if (state === PeriodState.approved) {
      return i18n.t('clock_in.header.actions.disapprove')
    } else {
      return i18n.t('clock_in.header.actions.approve')
    }
  }

  const borderStyle: BorderStyle = { color: 'grey400', width: 's1', style: 'solid' }

  let expandInfo: { text: string; icon: MediumIcon } = {
    text: i18n.t('time_tracking.table.headers.expand_all'),
    icon: 'chevron-down',
  }

  if (expandAll) {
    expandInfo = {
      text: i18n.t('time_tracking.table.headers.collapse_all'),
      icon: 'chevron-up',
    }
  }

  return (
    <Box
      flexDirection="row"
      justifyContent="spaceBetween"
      overflowX="hidden"
      overflowY="hidden"
      borderRadius={{
        topLeft: 'abs008',
        topRight: 'abs008',
      }}
      border={{
        top: borderStyle,
        left: borderStyle,
        right: borderStyle,
      }}
      background="grey000White"
      padding="s16"
    >
      <Box flexDirection="row" gap="s12" alignItems="center">
        <Text size="200" weight="semibold" caps>
          {i18n.t('common.state')}
        </Text>
        <Tag leftItem="circle" type={type} showCircle>
          {title}
        </Tag>
        <Text.Normal>{description}</Text.Normal>
      </Box>

      <Box flexDirection="row" gap="s8">
        {showApproveButton && (
          <Button onClick={handleConfirmPeriod} type="primary" disabled={loading}>
            {label()}
          </Button>
        )}
        {showAutofillButton && (
          <Button onClick={() => setShowAutofillModal(true)} type="secondary" disabled={loading}>
            {i18n.t('clock_in.header.autofill.button')}
          </Button>
        )}
        <Button
          onClick={() => {
            toggleExpandAll(!expandAll)()
            track(trackingEvents.timeTracking.expandOrCollapseAllDayRows, {
              action: expandInfo.text,
            })
          }}
          type="secondary"
          disabled={loading}
          iconName={expandInfo.icon}
        >
          {expandInfo.text}
        </Button>
      </Box>

      {showAutofillModal && (
        <Modal
          illustration="timer"
          title={i18n.t('clock_in.header.autofill.modal.title')}
          description={i18n.t('clock_in.header.autofill.modal.description')}
          onClose={() => setShowAutofillModal(false)}
          onSubmit={handleAutofill}
          footer={
            <>
              <Button type="secondary" onClick={() => setShowAutofillModal(false)}>
                {i18n.t('common.cancel')}
              </Button>
              <Button type="primary" disabled={loading} submit>
                {i18n.t('clock_in.header.autofill.modal.action')}
              </Button>
            </>
          }
        />
      )}
    </Box>
  )
}

export default observer(PeriodStatus)
